import * as React from "react";
import LazyLoad from "react-lazyload";
import { inject } from "mobx-react";
import EditorialBase from "components/flexComponents/Content/factory/EditorialBase";
import { UitkCardAloha, UitkPlaceholderCard } from "uitk-react-cards";
import { UitkText, UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { ContentFlexComponent, ContentProps } from "components/flexComponents/Content/typings";
import { Media } from "src/typings/microserviceModels/content-flex-module";
import { decorateEditorialHtml } from "components/flexComponents/Content/ContentUtilities";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage, UitkImageLink } from "uitk-react-experimental-images";
import { ThumborStore } from "src/stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { UitkScrimType, UitkScrim } from "uitk-react-scrim";
import { UitkAvatar } from "uitk-react-avatar";

const ImageDisplay = inject("thumbor")(
  ({ image, context, thumbor }: { image: Media; context: ExtendedContextStore; thumbor?: ThumborStore }) => {
    if (!thumbor) {
      return null;
    }

    const width = context.deviceInformation?.mobile ? 360 : 1040;

    const resizedImage = thumbor?.safelyGenerateThumborizedImageUrl(image.mediaUrl, width, undefined, context);

    return (
      <LazyLoad offset={500} height="100%" once={true} placeholder={<UitkPlaceholderCard />}>
        <UitkCardAloha>
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <UitkImage alt={image.mediaAlt} src={resizedImage} />
            {image.clickUrl && (
              <UitkImageLink>
                <a href={`${image.clickUrl}`} target="blank" />
              </UitkImageLink>
            )}
            {image.mediaCaption && (
              <UitkScrim type={UitkScrimType.BOTTOM}>
                <UitkText>{image.mediaCaption}</UitkText>
              </UitkScrim>
            )}
          </UitkFigure>
        </UitkCardAloha>
      </LazyLoad>
    );
  }
);

export class SocialMediaGridEditorial extends EditorialBase {
  public constructor(editorial: ContentFlexComponent) {
    super(editorial);
  }

  /**
   * An Editorial consists of HTML strings that we parse through with the best of
   * our abilities and display as is on the page. Mainly, we do not control the
   * content of an Editorial, we simply put them in the page.
   */
  public generate(props: ContentProps) {
    const { context } = props;
    const { title: moduleTitle } = this.editorial;
    const { media, title, subtitle, text } = this.editorial.items[0];

    const socialMediaImages = media?.filter((image) => {
      return image.imageType === "General Image";
    });

    const socialMediaAvatar = media?.filter((image) => {
      return image.imageType === "Logo";
    })[0];

    if (!media || media.length === 0) {
      return null;
    }

    return (
      <UitkSpacing padding={{ block: "three" }}>
        <div
          id={this.flexComponent.id}
          data-fm={this.flexComponent.fmId}
          data-fm-title-id={this.flexComponent.fmTitleId}
          className="SocialMediaGridEditorial"
        >
          <>
            {moduleTitle && (
              <UitkSpacing margin={{ block: "two", inline: "three" }}>
                <div>
                  <UitkHeading level={2} size={500}>
                    {moduleTitle}
                  </UitkHeading>
                </div>
              </UitkSpacing>
            )}
            <UitkLayoutFlex alignContent={"start"} alignItems={"center"}>
              <>
                {socialMediaAvatar && (
                  <UitkLayoutFlexItem maxWidth={"full_width"}>
                    <a href={socialMediaAvatar.clickUrl}>
                      <UitkAvatar
                        image={{ alt: socialMediaAvatar.mediaAlt, src: socialMediaAvatar.mediaUrl }}
                        border={true}
                        size="large"
                      />
                    </a>
                  </UitkLayoutFlexItem>
                )}
                {title && (
                  <UitkLayoutFlexItem>
                    <UitkSpacing margin={{ inline: "one" }} padding={{ block: "two" }}>
                      <div>
                        <UitkHeading level={3} size={500}>
                          {title}
                        </UitkHeading>
                        {subtitle && <UitkSubheading>{subtitle}</UitkSubheading>}
                      </div>
                    </UitkSpacing>
                  </UitkLayoutFlexItem>
                )}
              </>
            </UitkLayoutFlex>
          </>
          {text && (
            <UitkSpacing margin={{ blockstart: "two", blockend: "two" }}>
              <UitkText size={300}>
                <div dangerouslySetInnerHTML={decorateEditorialHtml(text)} />
              </UitkText>
            </UitkSpacing>
          )}

          <UitkLayoutGrid columns={{ small: 3, medium: 6 }} justifyContent="space-around" space="two">
            {socialMediaImages &&
              socialMediaImages.length >= 6 &&
              socialMediaImages.length < 12 &&
              socialMediaImages.slice(0, 6).map((item) => {
                return (
                  <UitkLayoutGridItem key={item.mediaUrl}>
                    <ImageDisplay image={item} context={context} />;
                  </UitkLayoutGridItem>
                );
              })}
            {socialMediaImages &&
              socialMediaImages.length >= 12 &&
              media.slice(0, 12).map((item) => {
                return (
                  <UitkLayoutGridItem key={item.mediaUrl}>
                    <ImageDisplay image={item} context={context} />;
                  </UitkLayoutGridItem>
                );
              })}
          </UitkLayoutGrid>
        </div>
      </UitkSpacing>
    );
  }
}

export default SocialMediaGridEditorial;
